/**
 * Display-only translation for the fixed enum/status values that come back from API data
 * (appointment status, visit type, order priority, clinical alert severity, etc.) across the
 * clinical pages (DoctorEMRPage.tsx, DischargeSummaryPage.tsx, and similar).
 *
 * IMPORTANT — this only changes what's shown on screen. The actual value stored in the
 * database (e.g. a record's status='ACTIVE') and sent back to the API is never touched — the
 * raw English enum string is still what's compared against in filters/onChange handlers
 * (`v.status==='COMPLETED'`, etc.) and saved on write. This is the same "translate the label,
 * not the data" approach already used for discharge status and condition/disposition dropdowns
 * in DischargeSummaryPage.tsx — safe because these are fixed system enums, not free text a
 * clinician typed, so there's no ambiguity about what the "right" translation is.
 *
 * Usage: getStatusLabel(v.status, t)
 * Add new values to STATUS_FALLBACK below as they're found — an unlisted value still displays
 * (falls back to the raw value itself) rather than breaking, so this can grow incrementally.
 */

// Raw enum value -> readable English fallback (used both as the i18next fallback text and as
// what's shown if a value isn't in this dictionary at all).
const STATUS_FALLBACK: Record<string, string> = {
  // Generic lifecycle — reused across appointments, referrals, care plans, diagnoses, orders...
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  PENDING: 'Pending',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  ONGOING: 'Ongoing',
  RESOLVED: 'Resolved',
  CONTROLLED: 'Controlled',
  REMISSION: 'Remission',
  VERIFIED: 'Verified',
  DRAFT: 'Draft',
  FINALIZED: 'Finalized',
  APPROVED: 'Approved',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  TRANSFERRED: 'Transferred',
  // Appointment-specific
  SCHEDULED: 'Scheduled',
  CHECKED_IN: 'Checked In',
  IN_CONSULTATION: 'In Consultation',
  NO_SHOW: 'No Show',
  NEEDS_RESCHEDULE: 'Needs Reschedule',
  // Visit / encounter type
  OPD: 'OPD',
  IPD: 'IPD',
  EMERGENCY: 'Emergency',
  SURGERY: 'Surgery',
  APPOINTMENT: 'Appointment',
  // Order priority / status (CPOE etc.)
  ROUTINE: 'Routine',
  URGENT: 'Urgent',
  STAT: 'STAT',
  ORDERED: 'Ordered',
  IN_PROGRESS: 'In Progress',
  // Clinical severity
  MILD: 'Mild',
  MODERATE: 'Moderate',
  SEVERE: 'Severe',
  LIFE_THREATENING: 'Life Threatening',
  // Allergy type
  DRUG: 'Drug',
  FOOD: 'Food',
  ENVIRONMENTAL: 'Environmental',
  OTHER: 'Other',
  // Clinical alert type / severity
  DRUG_ALLERGY: 'Drug Allergy',
  DRUG_INTERACTION: 'Drug Interaction',
  CRITICAL_VALUE: 'Critical Value',
  HIGH_RISK: 'High Risk',
  CUSTOM: 'Custom',
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

export function getStatusLabel(value: string | undefined | null, t: any): string {
  if (!value) return '';
  const fallback = STATUS_FALLBACK[value] || value;
  return t(`status.${value}`, fallback);
}
